// Pilot Program Application Service
// Handles API calls for pilot program applications

import { config } from "@/config";

// Types matching backend enums
export type CompanySize = "1-10" | "11-50" | "51-200" | "201-500" | "500+";

export type Industry =
  | "Agriculture / Farming"
  | "Brewing / Distilling"
  | "Food Processing"
  | "Restaurant / Food Service"
  | "Manufacturing"
  | "Logistics / Warehousing"
  | "Environmental Monitoring"
  | "Other";

export type ReferralSource =
  | "Search Engine"
  | "Social Media"
  | "Industry Event / Conference"
  | "Referral from colleague"
  | "News / Press"
  | "Other";

export type NumberOfLocations = "1" | "2-5" | "6-10" | "11-50" | "50+";

export interface PilotApplicationData {
  fullName: string;
  email: string;
  phone?: string;
  companyName: string;
  companySize: CompanySize;
  industry: Industry;
  numberOfLocations: NumberOfLocations;
  useCase: string;
  referralSource: ReferralSource;
}

export interface PilotApplicationResponse {
  success: boolean;
  data?: {
    applicationId: string;
    email: string;
    companyName: string;
    submittedAt: string;
  };
  message: string;
}

export interface PilotApplicationError {
  status: string;
  message: string;
  code?: string;
  details?: Array<{
    type: string;
    msg: string;
    path: string;
    location: string;
  }>;
}

/**
 * Submit a pilot program application
 */
export async function submitPilotApplication(
  data: PilotApplicationData,
): Promise<PilotApplicationResponse> {
  const response = await fetch(`${config.api.baseUrl}/pilot/apply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    const error = result as PilotApplicationError;

    // Handle validation errors
    if (error.details && error.details.length > 0) {
      const fieldErrors = error.details.map((d) => d.msg).join(", ");
      throw new Error(fieldErrors);
    }

    throw new Error(error.message || "Failed to submit application");
  }

  return result as PilotApplicationResponse;
}

// Dropdown options for form selects
export const COMPANY_SIZE_OPTIONS: Array<{ value: CompanySize; label: string }> = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "500+", label: "500+ employees" },
];

export const INDUSTRY_OPTIONS: Array<{ value: Industry; label: string }> = [
  { value: "Agriculture / Farming", label: "Agriculture / Farming" },
  { value: "Brewing / Distilling", label: "Brewing / Distilling" },
  { value: "Food Processing", label: "Food Processing" },
  { value: "Restaurant / Food Service", label: "Restaurant / Food Service" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Logistics / Warehousing", label: "Logistics / Warehousing" },
  { value: "Environmental Monitoring", label: "Environmental Monitoring" },
  { value: "Other", label: "Other" },
];

export const REFERRAL_SOURCE_OPTIONS: Array<{ value: ReferralSource; label: string }> = [
  { value: "Search Engine", label: "Search Engine (Google, Bing, etc.)" },
  { value: "Social Media", label: "Social Media" },
  { value: "Industry Event / Conference", label: "Industry Event / Conference" },
  { value: "Referral from colleague", label: "Referral from colleague" },
  { value: "News / Press", label: "News / Press" },
  { value: "Other", label: "Other" },
];

export const NUMBER_OF_LOCATIONS_OPTIONS: Array<{ value: NumberOfLocations; label: string }> = [
  { value: "1", label: "1 location" },
  { value: "2-5", label: "2-5 locations" },
  { value: "6-10", label: "6-10 locations" },
  { value: "11-50", label: "11-50 locations" },
  { value: "50+", label: "50+ locations" },
];
